import { uniqBy } from 'lodash'
import { useMemo } from 'react'
import { Patient, Parent } from '@/core/models/patient.model'

type ParentInfo = {
  allParents: Parent[]
  hasSharedParents: boolean
  canSelectParents: boolean
}

const emptyParentInfo: ParentInfo = {
  allParents: [],
  hasSharedParents: false,
  canSelectParents: false,
}

type UseParentInfoProps = {
  primaryPatient?: Patient
  secondaryPatient?: Patient
}

export function useParentInfo({ primaryPatient, secondaryPatient }: UseParentInfoProps): ParentInfo {
  const parentInfo = useMemo(() => {
    if (!primaryPatient || !secondaryPatient) return emptyParentInfo

    const primary = primaryPatient.parents || []
    const secondary = secondaryPatient.parents || []

    if (primary.length === 0 && secondary.length === 0) return emptyParentInfo

    const uniqueParents = uniqBy([...primary, ...secondary], generateUniqueKey)

    const primaryKeys = new Set(primary.map(generateUniqueKey))
    const secondaryKeys = new Set(secondary.map(generateUniqueKey))
    const hasSharedParents = secondary.some((p) => primaryKeys.has(generateUniqueKey(p)))

    const sameSize = primaryKeys.size === secondaryKeys.size
    const sameContent = sameSize && Array.from(primaryKeys).every((k) => secondaryKeys.has(k))
    const canSelectParents = !sameContent

    return {
      allParents: uniqueParents,
      hasSharedParents,
      canSelectParents,
    }
  }, [primaryPatient, secondaryPatient])

  return parentInfo
}

function generateUniqueKey(parent: Parent) {
  return `${(parent.firstname || '').trim().toLowerCase()}|${(parent.lastname || '').trim().toLowerCase()}|${(parent.email || '').trim().toLowerCase()}|${(
    parent.phone || ''
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')}`
}
